import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box } from '@mui/material';
import ExoForm from '../../ExoForm';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { API_PATCH, API_POST, DBRequest, useConfig } from '../../../../api/api';
import useFetch from '../../../../api/useFetch';
import { CALENDAR_PROVIDERS } from '../../../../config/calendarProviders';

/**
 * Create / edit a calendar.
 *
 * With `syncOnly` the form only manages the current user's personal sync settings. This is
 * used for calendars that were shared read-only: the user may mirror them into their own
 * Google / Microsoft calendar, but may not change the calendar itself.
 */
export const NewCalendar = ({
  onClose = () => {},
  onCreate = () => {},
  open,
  startData,
  syncOnly = false,
}) => {
  const { t } = useTranslation();
  const config = useConfig();

  const { apiData } = useFetch('integrations');
  const [connectedProviders, setConnectedProviders] = useState([]);

  useEffect(() => {
    if (apiData) {
      const connected = apiData?.data
        .filter(
          (el) =>
            Object.keys(CALENDAR_PROVIDERS).includes(el.type) &&
            !el.actionRequired
        )
        .map((el) => el.type);
      setConnectedProviders(connected);
    }
  }, [apiData]);

  const [isLoading, setIsLoading] = useState(false);

  // Transform startData to convert null/undefined syncProvider to 'none'
  const transformedStartData = useMemo(() => {
    if (!startData) return startData;
    return {
      ...startData,
      syncProvider: startData.syncProvider || 'none',
    };
  }, [startData]);

  // Build sync provider options with "No sync" as first option
  const syncProviderOptions = useMemo(() => [
    { label: t('No sync'), value: 'none' },
    ...connectedProviders.map((providerType) => ({
      label: CALENDAR_PROVIDERS[providerType]?.name || providerType,
      value: providerType,
    })),
  ], [connectedProviders, t]);

  const fields = useMemo(() => {
    const syncField = {
      label: t('Sync to'),
      key: 'syncProvider',
      type: 'customSelect',
      options: syncProviderOptions,
      noUnselect: true,
      default: 'none',
    };

    if (syncOnly) return [syncField];

    return [
      {
        label: t('Color'),
        key: 'color',
        type: 'color',
      },
      {
        label: t('Name'),
        key: 'name',
        type: 'text',
      },
      {
        label: t('Every employee should have this calendar'),
        key: 'isPublic',
        type: 'checkbox',
      },
      ...(connectedProviders.length > 0 ? [syncField] : []),
    ];
  }, [t, connectedProviders, syncProviderOptions, syncOnly]);

  const validationSchema = syncOnly
    ? Yup.object().shape({})
    : Yup.object().shape({
        color: Yup.string().required('Is required'),
        name: Yup.string().required('Is required'),
      });

  function handleSubmit(data) {
    const isUpdate = data.id ? true : false;

    // Derive shouldSync from whether a provider is selected (not 'none')
    const hasProvider = data.syncProvider && data.syncProvider !== 'none';
    const syncSettings = {
      shouldSync: Boolean(hasProvider),
      syncProvider: hasProvider ? data.syncProvider : null,
    };

    // Read-only calendars only accept the personal sync settings
    const payload = syncOnly
      ? syncSettings
      : { ...data, isPublic: data.isPublic ?? false, ...syncSettings };

    DBRequest({
      config,
      path: isUpdate ? `calendars/${data.id}` : 'calendars',
      method: isUpdate ? API_PATCH(payload) : API_POST(payload),
      onResponse: (res) => onCreate(res.data),
      onLoading: setIsLoading,
    });
  }

  const header = syncOnly
    ? t('Synchronize Calendar')
    : startData?.id
      ? t('Edit Calendar')
      : t('New Calendar');

  const form = (
    <ExoForm
      isLoading={isLoading}
      fields={fields}
      onSubmit={handleSubmit}
      onCancel={onClose}
      noDifferences
      startDataCollection={transformedStartData}
      header={header}
      validationSchema={validationSchema}
    />
  );

  if (!syncOnly) return form;

  return (
    <Box className="flex flex-col gap-2">
      <Alert severity="info">
        {connectedProviders.length > 0
          ? t(
              'This calendar was shared with you without write permissions. Its events are copied to the selected calendar service, but changes you make there are not imported into ExoPaper and will be reverted.'
            )
          : t(
              'No calendar service is connected yet. Connect Google Calendar or Microsoft 365 in your profile first.'
            )}
      </Alert>
      {form}
    </Box>
  );
};
